// FLOW-12 (Task 4.2, G2, G3, R3) — as pegadas do flow: quem decide se a entrada guardada de um passo
// ainda vale (o ▶ roda só ele) ou se os de cima rodam de novo, e quais passos ficam "Mudou depois da
// execução". É o nó "dirty" do n8n, decidido no editor, enquanto se digita e antes de salvar.
//
// A pegada de um passo é um hash de 53 bits (cyrb53, síncrono) do JSON canônico do nó do YAML, com as
// chaves em ordem e sem o conteúdo das listas de passos de dentro dele, mais o da saída fixada dele.
// Comentário, recuo e estilo do YAML não mudam a pegada: ela sai do valor, e não do texto. A do
// gatilho é a dos `params` do YAML e dos dados do teste usados.
//
// O editor manda todas as pegadas em todo `depurar`, e o PowerTools as guarda na sessão como texto
// opaco (até 300, de até 16 caracteres). O jsonb do Postgres não guarda a ordem das chaves, e a G2
// compara a lista ORDENADA dos passos acima: por isso cada pegada de passo leva a posição dele na
// ordem do documento, `<posição>.<hash>`, as duas em base 36 (até 14 caracteres). A do gatilho é só
// o hash. Com a posição, a ordem da sessão se refaz a partir das pegadas dela, sem o YAML de então.
import { isMap } from 'yaml';
import { documentoLido } from './leituraYaml';
import { estaSolto, percorrer } from './documentoYaml';
import { grafoDoFlow } from './grafoDoFlow';

export const GATILHO = 'gatilho';
// A janela de reuso da entrada guardada (o `REUSO_S` do PowerTools): a sessão de mais de 1 h não serve
// ao ▶, e o PowerTools daria 409 (R4).
export const REUSO_MS = 60 * 60 * 1000;

// As chaves de um passo que guardam listas de passos (o `$defs/passos` do `flow.schema.json`), em
// caminho: `cases.*` é cada caso do `switch`, e `branches[].steps` os passos de cada ramo. O spec
// confere a lista contra o schema, como a lista dos campos de fórmula da G17 da FLOW-11: uma lista
// nova no schema que não entra aqui faria a pegada do bloco mudar a cada edição dentro dele.
export const LISTAS_DE_PASSOS = [
  'then',
  'else',
  'default',
  'cases.*',
  'steps',
  'branches[].steps',
  'on_error.steps',
];

// O que fica no lugar de uma lista de passos: ter a lista (o `else`, a rota de erro) muda o bloco;
// o que há nela, não.
const LISTA = '[passos]';
// Os dados do teste que contam na pegada do gatilho (G2). O `fixado` é só se a gaveta abre.
const DADOS_DA_PEGADA = ['params', 'contexto', 'conversa', 'agente'];

const ehObjeto = valor =>
  !!valor && typeof valor === 'object' && !Array.isArray(valor);

// cyrb53, de bryc (domínio público): 53 bits, síncrono e sem dependência. Basta para dizer se um
// passo mudou; não é segurança. Um hash é feito de operações de bit.
/* eslint-disable no-bitwise */
function cyrb53(texto) {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < texto.length; i += 1) {
    const ch = texto.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}
/* eslint-enable no-bitwise */

// O JSON com as chaves em ordem, em qualquer nível.
export function canonico(valor) {
  if (Array.isArray(valor)) return `[${valor.map(canonico).join(',')}]`;
  if (ehObjeto(valor)) {
    const pares = Object.keys(valor)
      .sort()
      .map(chave => `${JSON.stringify(chave)}:${canonico(valor[chave])}`);
    return `{${pares.join(',')}}`;
  }
  return JSON.stringify(valor ?? null) ?? 'null';
}

const hash = texto => cyrb53(texto).toString(36);

// O nó sem o conteúdo das listas de passos, pelo caminho de `LISTAS_DE_PASSOS`. Só a lista troca: o
// valor que não é lista (o YAML pela metade, enquanto se digita) fica, e conta como o resto do nó.
function semListas(valor, partes) {
  const [parte, ...resto] = partes;
  const trocar = v => {
    if (resto.length) return semListas(v, resto);
    return Array.isArray(v) ? LISTA : v;
  };
  if (!ehObjeto(valor)) return valor;
  if (parte === '*') {
    return Object.fromEntries(
      Object.entries(valor).map(([chave, v]) => [chave, trocar(v)])
    );
  }
  if (parte.endsWith('[]')) {
    const chave = parte.slice(0, -2);
    if (!Array.isArray(valor[chave])) return valor;
    return { ...valor, [chave]: valor[chave].map(trocar) };
  }
  if (!(parte in valor)) return valor;
  return { ...valor, [parte]: trocar(valor[parte]) };
}

const PARTES = LISTAS_DE_PASSOS.map(caminho =>
  caminho.replace(/\[\]/g, '[].').split('.').filter(Boolean)
);
const doNo = no =>
  PARTES.reduce((valor, partes) => semListas(valor, partes), no);

// Os passos do flow na ordem do documento (o `percorrer`, o mesmo do `idsAntesDe`), cada um com o
// caminho estático (o id do nó do canvas e o `caminho` dos eventos) e o nó em JSON. As correntes
// soltas (`detached`) ficam de fora: não rodam, e nada do que elas têm chega a outro passo.
export function passosNaOrdem(texto) {
  const caminhos = Object.fromEntries(
    grafoDoFlow(texto)
      .nos.filter(no => no.type === 'passo')
      .map(no => [no.data.ponteiro, no.data.caminho])
  );
  const passos = [];
  percorrer(documentoLido(texto).doc, (passo, ponteiro) => {
    if (estaSolto(ponteiro) || !caminhos[ponteiro]) return;
    passos.push({ caminho: caminhos[ponteiro], no: passo.toJSON() });
  });
  return passos;
}

function paramsDoYaml(texto) {
  const raiz = documentoLido(texto).doc.contents;
  const params = isMap(raiz) ? raiz.get('params', true) : null;
  return params && typeof params.toJSON === 'function'
    ? params.toJSON()
    : (params ?? null);
}

// `{caminho: pegada}` de cada passo, mais a do gatilho (`GATILHO`). `fixados` é o mapa do servidor
// (`{id do passo: saída}`); `dados`, os dados do teste (`params`, `contexto`, `conversa`, `agente`).
export function pegadasDoFlow(texto, fixados = {}, dados = {}) {
  const fixadosDe = ehObjeto(fixados) ? fixados : {};
  const dadosDe = ehObjeto(dados) ? dados : {};
  const usados = Object.fromEntries(
    DADOS_DA_PEGADA.map(chave => [chave, dadosDe[chave] ?? null])
  );
  const pegadas = {
    [GATILHO]: hash(canonico({ params: paramsDoYaml(texto), dados: usados })),
  };
  passosNaOrdem(texto).forEach(({ caminho, no }, posicao) => {
    const id = ehObjeto(no) && typeof no.id === 'string' ? no.id : null;
    const fixado =
      id && Object.prototype.hasOwnProperty.call(fixadosDe, id)
        ? canonico(fixadosDe[id])
        : '';
    pegadas[caminho] =
      `${posicao.toString(36)}.${hash(`${canonico(doNo(no))}\u0000${fixado}`)}`;
  });
  return pegadas;
}

// O hash de uma pegada, sem a posição.
const hashDe = pegada =>
  typeof pegada === 'string' ? pegada.slice(pegada.indexOf('.') + 1) : null;

// Os caminhos das pegadas na ordem do documento de quando elas foram tiradas, pela posição. A
// pegada sem posição (de outro editor) não dá ordem: nulo.
export function ordemDasPegadas(pegadas) {
  if (!ehObjeto(pegadas)) return null;
  const passos = Object.entries(pegadas).filter(([c]) => c !== GATILHO);
  const comPosicao = passos.map(([caminho, pegada]) => ({
    caminho,
    posicao:
      typeof pegada === 'string' && pegada.includes('.')
        ? parseInt(pegada.slice(0, pegada.indexOf('.')), 36)
        : NaN,
  }));
  if (comPosicao.some(p => !Number.isInteger(p.posicao))) return null;
  return comPosicao.sort((a, b) => a.posicao - b.posicao).map(p => p.caminho);
}

// "Acima de X" numa ordem de caminhos (G2): os passos antes de X, o que traz os blocos que o contêm,
// e, se X está num laço, o corpo inteiro do laço (na volta seguinte, o que vem depois de X no corpo
// roda antes dele). Um passo de outro ramo também entra: a regra erra para o lado de rodar de novo,
// nunca para o de usar entrada velha. O laço sai do caminho: o corpo dele é `<laço>.steps[...]`, e
// nenhum outro bloco tem `steps` direto (o ramo é `.branches[r].steps`, a rota `.on_error.steps`).
// Nulo quando X não está na ordem.
export function acimaNaOrdem(ordem, caminho) {
  const i = (ordem || []).indexOf(caminho);
  if (i < 0) return null;
  const lacos = ordem.filter(c => caminho.startsWith(`${c}.steps[`));
  return ordem.filter(
    (c, k) =>
      k !== i && (k < i || lacos.some(laco => c.startsWith(`${laco}.steps[`)))
  );
}

// G2: os caminhos acima do passo, na ordem do documento. `detached` fica de fora.
export function acimaDe(texto, caminho) {
  return acimaNaOrdem(
    passosNaOrdem(texto).map(p => p.caminho),
    caminho
  );
}

// G2, pelas pegadas de agora: a entrada que a `sessao` guardou para o passo ainda vale. A sessão é
// `{criado_em, pegadas}` (do passo a passo, ou a que a página abriu) e é a de onde vem a entrada do
// passo. Vale quando ela é da última hora (`REUSO_MS`), a lista ordenada dos passos acima é a mesma,
// e a pegada de cada um deles e a do gatilho batem. A pegada do próprio passo não conta: editá-lo
// não muda o que chega nele.
export function entradaValePelasPegadas(
  atuais,
  caminho,
  sessao,
  agora = Date.now()
) {
  const antes = sessao?.pegadas;
  if (!ehObjeto(antes) || !ehObjeto(atuais)) return false;
  const criada = Date.parse(sessao.criado_em);
  if (!Number.isFinite(criada) || agora - criada >= REUSO_MS) return false;
  if (!(GATILHO in antes) || antes[GATILHO] !== atuais[GATILHO]) return false;
  const acimaAgora = acimaNaOrdem(ordemDasPegadas(atuais), caminho);
  const acimaAntes = acimaNaOrdem(ordemDasPegadas(antes), caminho);
  if (!acimaAgora || !acimaAntes || acimaAgora.length !== acimaAntes.length) {
    return false;
  }
  return acimaAgora.every(
    (c, k) => c === acimaAntes[k] && hashDe(antes[c]) === hashDe(atuais[c])
  );
}

// G2, a partir do texto: `opcoes` traz os `fixados`, os `dados` do teste e o `agora` (o teste o fixa).
export function entradaVale(texto, caminho, sessao, opcoes = {}) {
  const { fixados = {}, dados = {}, agora = Date.now() } = opcoes;
  return entradaValePelasPegadas(
    pegadasDoFlow(texto, fixados, dados),
    caminho,
    sessao,
    agora
  );
}

// O caminho da lista de um passo: `steps[a].then[b]` → `steps[a].then`. O último segmento de um
// passo é sempre `[id]` ou `[n]`.
const listaDe = caminho => caminho.slice(0, caminho.lastIndexOf('['));

// O passo logo antes deste na lista dele, numa ordem de caminhos; nulo no primeiro.
export function anteriorNaLista(ordem, caminho) {
  const irmaos = (ordem || []).filter(c => listaDe(c) === listaDe(caminho));
  const k = irmaos.indexOf(caminho);
  return k > 0 ? irmaos[k - 1] : null;
}

// G3, pelas pegadas de agora: o passo "Mudou depois da execução" da `sessao` de onde vem o status
// dele quando a pegada dele mudou (editado, fixado editado, desafixado), quando a sessão não a tem
// (passo novo) ou quando o passo logo antes dele na lista é outro (inserido, excluído ou movido
// acima). Editar um passo não marca os de baixo: quem roda os de cima de novo é a G2. Sem sessão, ou
// com uma sessão sem pegadas (de antes da FLOW-12), não há o que comparar: não mudou.
export function mudouPelasPegadas(atuais, caminho, sessao) {
  const antes = sessao?.pegadas;
  if (!ehObjeto(antes) || !Object.keys(antes).length || !ehObjeto(atuais)) {
    return false;
  }
  if (!(caminho in atuais)) return false;
  if (!(caminho in antes)) return true;
  if (hashDe(antes[caminho]) !== hashDe(atuais[caminho])) return true;
  if (caminho === GATILHO) return false;
  return (
    anteriorNaLista(ordemDasPegadas(antes), caminho) !==
    anteriorNaLista(ordemDasPegadas(atuais), caminho)
  );
}

// G3, a partir do texto, com os `fixados` e os `dados` de agora.
export function mudou(texto, caminho, sessao, opcoes = {}) {
  const { fixados = {}, dados = {} } = opcoes;
  return mudouPelasPegadas(
    pegadasDoFlow(texto, fixados, dados),
    caminho,
    sessao
  );
}
